<template>
  <div class="dw-page">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h3 class="page-title mb-1">Мои кошельки</h3>
        <div class="page-subtitle">
          Всего: <span class="subtitle-strong">{{ wallets.length }}</span>
        </div>
      </div>

      <div class="d-flex">
        <b-button
          variant="outline-light"
          class="btn-round mr-2"
          @click="fetchWallets"
          :disabled="loadingList"
        >
          ↻ Обновить
        </b-button>

        <b-button variant="primary" class="btn-round btn-primary-soft" @click="openCreate">
          + Добавить
        </b-button>
      </div>
    </div>

    <!-- Create Card -->
    <b-collapse :visible="showCreate">
      <div class="create-card mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="create-title">Создать цифровой кошелёк</div>
          <b-button size="sm" variant="outline-light" class="btn-x" @click="closeCreate">✕</b-button>
        </div>

        <div class="create-sub mb-3">
          Выберите тип и заполните реквизиты. Для <b>Банковского счёта</b> — номер счёта, для <b>Крипто</b> — хэш/адрес.
        </div>

        <b-row>
          <b-col md="4" class="mb-3">
            <label class="field-label">Тип кошелька</label>
            <b-form-select v-model="form.walletType" :options="walletTypeOptions" class="dark-input" />
          </b-col>

          <!-- Crypto: явный (asset, network) выбор из каталога.
               BTC / SOL / ETH имеют одну сеть — поле «Сеть» прячется
               и берётся автоматически по умолчанию. USDT / USDC
               показывают выбор сети (только название). -->
          <template v-if="form.walletType === 'Crypto'">
            <b-col md="4" class="mb-3">
              <label class="field-label">Актив</label>
              <b-form-select
                v-model="form.assetCode"
                :options="assetOptions"
                class="dark-input"
                @change="onAssetChange"
              />
            </b-col>
            <b-col md="4" class="mb-3" v-if="networkOptionsForSelected.length > 1">
              <label class="field-label">Сеть</label>
              <b-form-select
                v-model="form.networkCode"
                :options="networkOptionsForSelected"
                class="dark-input"
              />
            </b-col>
            <b-col md="4" class="mb-3" v-else-if="networkOptionsForSelected.length === 1">
              <label class="field-label">Сеть</label>
              <div class="dark-input dark-input--readonly">
                {{ networkOptionsForSelected[0].text }}
              </div>
            </b-col>
          </template>

          <b-col md="12" class="mb-3">
            <label class="field-label">
              {{ form.walletType === 'BankAccount' ? 'Банковский счёт' : 'Крипто-адрес / Хэш' }}
            </label>
            <b-input-group class="dark-input-group">
              <b-form-input
                v-model="primaryValue"
                class="dark-input"
                :placeholder="form.walletType === 'BankAccount'
                  ? 'Введите номер счёта / IBAN'
                  : addressPlaceholder"
              />
              <b-input-group-append>
                <b-button variant="outline-light" class="btn-ghost" @click="pasteFromClipboard">
                  Вставить
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <div class="field-hint">
              {{ form.walletType === 'BankAccount'
                ? 'Пример: KZxx....'
                : addressHint }}
            </div>
          </b-col>

          <b-col v-if="form.walletType === 'BankAccount'" md="12" class="mb-3">
            <label class="field-label">БИК банка</label>
            <b-form-input
              v-model="form.bik"
              class="dark-input"
              placeholder="Например: HSBKKZKX"
              maxlength="20"
            />
            <div class="field-hint">
              SWIFT/BIC код банка получателя. Для казахстанских банков — 8-11 символов.
            </div>
          </b-col>

          <b-col md="12" class="mb-3">
            <label class="field-label">Название кошелька</label>
            <b-form-input
              v-model="form.walletName"
              class="dark-input"
              placeholder="Например: Мой Halyk / Binance USDT / Кошелёк для зарплаты"
            />
          </b-col>
        </b-row>

        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div class="small-muted">
            Пользователь: <span class="subtitle-strong">{{ safeUserId }}</span>
          </div>

          <div class="d-flex mt-3 mt-md-0">
            <b-button variant="outline-light" class="btn-round mr-2" @click="resetForm" :disabled="creating">
              Очистить
            </b-button>

            <b-button
              variant="success"
              class="btn-round btn-success-soft"
              @click="createWallet"
              :disabled="creating || !canSubmit"
            >
              <span v-if="!creating">Сохранить</span>
              <span v-else>Сохранение…</span>
            </b-button>
          </div>
        </div>

        <div v-if="error" class="alert-soft danger mt-3">
          <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
          {{ error }}
        </div>

        <div v-if="success" class="alert-soft success mt-3">
          <b-icon icon="check-circle" class="mr-2"></b-icon>
          {{ success }}
        </div>
      </div>
    </b-collapse>

    <!-- ✅ Split lists -->
    <div class="dw-split">
      <!-- Bank accounts -->
      <div class="dw-section bank">
        <div class="dw-section-head">
          <div class="dw-sec-title">
            🏦 Банковские счета
            <span class="dw-sec-count">{{ bankWallets.length }}</span>
          </div>
          <div class="dw-sec-sub">Счета / IBAN для фиатных переводов</div>
        </div>

        <b-row>
          <b-col
            v-for="w in bankWallets"
            :key="'bank-' + w.digitalWalletId"
            lg="4"
            md="6"
            class="mb-4"
          >
            <div class="dw-card dw-card-bank">
              <div class="dw-header">
                <div class="type-badge badge-bank">
                  🏦 Банковский счёт
                </div>
                <div class="dw-actions">
                  <button
                    v-if="editingId !== w.digitalWalletId"
                    type="button"
                    class="dw-icon-btn"
                    title="Переименовать"
                    @click="startEdit(w)"
                  >✏️</button>
                  <button
                    type="button"
                    class="dw-icon-btn dw-icon-btn-danger"
                    title="Удалить"
                    @click="confirmDelete(w)"
                  >🗑</button>
                </div>
              </div>

              <div v-if="editingId === w.digitalWalletId" class="dw-title-edit">
                <b-form-input
                  v-model="editingName"
                  class="dark-input"
                  size="sm"
                  placeholder="Название кошелька"
                  @keydown.enter="saveRename(w)"
                  @keydown.esc="cancelEdit"
                />
                <b-button size="sm" variant="primary" class="ml-2" :disabled="editingBusy" @click="saveRename(w)">✓</b-button>
                <b-button size="sm" variant="outline-light" class="ml-1" :disabled="editingBusy" @click="cancelEdit">×</b-button>
              </div>
              <div v-else class="dw-title">{{ w.walletName || 'Без названия' }}</div>

              <div class="dw-value">
                <span class="mono">{{ displayValue(w) }}</span>
                <b-button
                  size="sm"
                  variant="outline-light"
                  class="btn-copy btn-copy-bank"
                  @click="copyToClipboard(displayValue(w))"
                  :disabled="!displayValue(w)"
                >
                  <b-icon icon="clipboard"></b-icon>
                </b-button>
              </div>

              <div class="dw-meta">
                <div v-if="w.bik"><span class="label">БИК:</span><span class="value mono">{{ w.bik }}</span></div>
                <div><span class="label">Создан:</span><span class="value">{{ formatDate(w.createdDate) }}</span></div>
              </div>
            </div>
          </b-col>
        </b-row>

        <div v-if="!loadingList && bankWallets.length === 0" class="empty-mini empty-mini-bank">
          Банковских счетов нет.
        </div>
      </div>

      <!-- Crypto wallets -->
      <div class="dw-section crypto">
        <div class="dw-section-head">
          <div class="dw-sec-title">
            🪙 Крипто-кошельки
            <span class="dw-sec-count">{{ cryptoWallets.length }}</span>
          </div>
          <div class="dw-sec-sub">Адреса / хэши для криптовалют</div>
        </div>

        <b-row>
          <b-col
            v-for="w in cryptoWallets"
            :key="'crypto-' + w.digitalWalletId"
            lg="4"
            md="6"
            class="mb-4"
          >
            <div class="dw-card dw-card-crypto">
              <div class="dw-header">
                <div class="type-badge badge-crypto">
                  🪙 {{ w.assetCode || 'Крипто' }}<span v-if="w.networkCode" class="badge-net">{{ w.networkCode }}</span>
                </div>
                <div class="dw-actions">
                  <button
                    v-if="editingId !== w.digitalWalletId"
                    type="button"
                    class="dw-icon-btn"
                    title="Переименовать"
                    @click="startEdit(w)"
                  >✏️</button>
                  <button
                    type="button"
                    class="dw-icon-btn dw-icon-btn-danger"
                    title="Удалить"
                    @click="confirmDelete(w)"
                  >🗑</button>
                </div>
              </div>

              <div v-if="editingId === w.digitalWalletId" class="dw-title-edit">
                <b-form-input
                  v-model="editingName"
                  class="dark-input"
                  size="sm"
                  placeholder="Название кошелька"
                  @keydown.enter="saveRename(w)"
                  @keydown.esc="cancelEdit"
                />
                <b-button size="sm" variant="primary" class="ml-2" :disabled="editingBusy" @click="saveRename(w)">✓</b-button>
                <b-button size="sm" variant="outline-light" class="ml-1" :disabled="editingBusy" @click="cancelEdit">×</b-button>
              </div>
              <div v-else class="dw-title">{{ w.walletName || 'Без названия' }}</div>

              <div class="dw-value">
                <span class="mono">{{ displayValue(w) }}</span>
                <b-button
                  size="sm"
                  variant="outline-light"
                  class="btn-copy btn-copy-crypto"
                  @click="copyToClipboard(displayValue(w))"
                  :disabled="!displayValue(w)"
                >
                  <b-icon icon="clipboard"></b-icon>
                </b-button>
              </div>

              <div class="dw-meta">
                <div v-if="w.assetCode"><span class="label">Актив:</span><span class="value">{{ w.assetCode }}</span></div>
                <div v-if="w.networkCode"><span class="label">Сеть:</span><span class="value">{{ w.networkCode }}</span></div>
                <div><span class="label">Создан:</span><span class="value">{{ formatDate(w.createdDate) }}</span></div>
              </div>
            </div>
          </b-col>
        </b-row>

        <div v-if="!loadingList && cryptoWallets.length === 0" class="empty-mini empty-mini-crypto">
          Крипто-кошельков нет.
        </div>
      </div>
    </div>

    <!-- Empty (all) -->
    <div v-if="!loadingList && wallets.length === 0" class="empty">
      <div class="empty-icon">💳</div>
      <div class="empty-title">Цифровые кошельки не найдены</div>
      <div class="empty-subtitle">Нажмите «Добавить», чтобы создать первый кошелёк.</div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="deleteTarget" class="dw-modal-overlay" @click.self="closeDeleteModal">
      <div class="dw-modal">
        <div class="dw-modal-head">
          Удалить кошелёк?
          <button type="button" class="dw-modal-close" @click="closeDeleteModal">×</button>
        </div>
        <div class="dw-modal-body">
          <div class="dw-modal-name">{{ deleteTarget.walletName || 'Без названия' }}</div>
          <div class="dw-modal-addr mono">{{ displayValue(deleteTarget) }}</div>
        </div>
        <div class="dw-modal-foot">
          <b-button variant="outline-light" @click="closeDeleteModal" :disabled="deleteBusy">Отмена</b-button>
          <b-button variant="danger" class="ml-2" @click="submitDelete" :disabled="deleteBusy">
            {{ deleteBusy ? 'Удаляю…' : 'Удалить' }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { apiDomain, getHeader } from "@/config/config";
import { mapState } from "vuex";

export default {
  name: "DigitalWallet",
  data() {
    return {
      wallets: [],
      loadingList: false,
      creating: false,
      showCreate: false,

      // Inline-rename state
      editingId: null,
      editingName: "",
      editingBusy: false,

      // Delete confirmation modal
      deleteTarget: null,
      deleteBusy: false,

      // form
      form: {
        walletType: "BankAccount",
        walletName: "",
        // (asset, network) — обязательны для Crypto, скрыты для BankAccount.
        assetCode: "",
        networkCode: "",
        // BIK — обязателен для BankAccount, скрыт для Crypto.
        bik: ""
      },
      primaryValue: "",

      // Asset / network catalogue from GET /exchange/asset-networks.
      // Fetched once on mount — список монет короткий, кешируется
      // в state до перезагрузки страницы.
      assets: [],

      error: "",
      success: ""
    };
  },
  computed: {
    ...mapState(["loginedUser"]),
    safeUserId() {
      return this.loginedUser && this.loginedUser.id ? this.loginedUser.id : null;
    },
    walletTypeOptions() {
      return [
        { value: "BankAccount", text: "Банковский счёт" },
        { value: "Crypto", text: "Крипто" }
      ];
    },
    // Dropdown options for the asset picker.
    assetOptions() {
      const items = (this.assets || []).map(asset => ({
        value: asset.code,
        text: `${asset.symbol} — ${asset.name}`,
      }));
      return [{ value: "", text: "Выберите актив", disabled: true }, ...items];
    },
    // Networks available for the currently selected asset; empty
    // when no asset is chosen yet. Показываем только название сети
    // (со стандартом токена для стейблов), без комиссии.
    networkOptionsForSelected() {
      const code = this.form.assetCode;
      if (!code) return [];
      const asset = (this.assets || []).find(item => item.code === code);
      if (!asset || !asset.networks) return [];
      return asset.networks.map(network => {
        const std = network.tokenStandard ? ` (${network.tokenStandard})` : "";
        return { value: network.networkCode, text: network.networkName + std };
      });
    },
    // Friendly placeholder + hint that depend on the chosen asset, so
    // the user understands what an EVM address vs a TRC20 address vs
    // a Solana pubkey actually looks like.
    addressPlaceholder() {
      const code = this.form.assetCode;
      if (code === "BTC")  return "Например: bc1q… или 1A1zP…";
      if (code === "SOL" || this.form.networkCode === "SOLANA") return "Например: 5xJ8…sQ6";
      if (this.form.networkCode === "TRC20") return "Например: TXYZ…123";
      return "Например: 0x… (EVM-адрес)";
    },
    addressHint() {
      const network = this.form.networkCode;
      if (!network) return "Сначала выберите актив и сеть.";
      if (network === "TRC20")  return "Адреса TRC20 начинаются с T...";
      if (network === "SOLANA") return "Solana-адрес — длинная base58-строка.";
      if (network === "BITCOIN") return "Bitcoin-адрес начинается с 1, 3 или bc1.";
      return "EVM-адрес начинается с 0x...";
    },
    canSubmit() {
      if (!this.safeUserId) return false;
      if (!this.form.walletType) return false;
      if (!this.primaryValue || !String(this.primaryValue).trim()) return false;
      if (!this.form.walletName || !String(this.form.walletName).trim()) return false;
      if (this.form.walletType === "Crypto") {
        if (!this.form.assetCode || !this.form.networkCode) return false;
      }
      if (this.form.walletType === "BankAccount") {
        if (!this.form.bik || !String(this.form.bik).trim()) return false;
      }
      return true;
    },

    // ✅ split lists
    bankWallets() {
      return (this.wallets || []).filter((w) => this.normalizeType(w.walletType) === "BankAccount");
    },
    cryptoWallets() {
      return (this.wallets || []).filter((w) => this.normalizeType(w.walletType) !== "BankAccount");
    }
  },
  methods: {
    openCreate() {
      this.showCreate = true;
      this.error = "";
      this.success = "";
    },
    closeCreate() {
      this.showCreate = false;
      this.error = "";
      this.success = "";
    },
    resetForm() {
      this.form.walletType = "BankAccount";
      this.form.walletName = "";
      this.form.assetCode = "";
      this.form.networkCode = "";
      this.form.bik = "";
      this.primaryValue = "";
      this.error = "";
      this.success = "";
    },
    // Asset/network catalogue is a one-shot load — list is short and
    // doesn't change between sessions. Saved into `assets` for the
    // dropdown options + onAssetChange auto-pick.
    fetchCatalog() {
      axios
        .get(apiDomain + "/exchange/asset-networks")
        .then((res) => { this.assets = Array.isArray(res.data) ? res.data : []; })
        .catch(() => { this.assets = []; });
    },
    // On asset change: preselect the catalogue-marked default network
    // so the user doesn't have to click twice for single-network assets
    // (BTC / ETH / SOL).
    onAssetChange() {
      const asset = (this.assets || []).find(item => item.code === this.form.assetCode);
      if (!asset || !asset.networks || !asset.networks.length) {
        this.form.networkCode = "";
        return;
      }
      const def = asset.networks.find(n => n.isDefault) || asset.networks[0];
      this.form.networkCode = def.networkCode;
    },

    normalizeType(t) {
      var v = (t || "").toString();
      if (!v) return "Crypto";
      v = v.toLowerCase();
      if (v === "bankaccount" || v === "bank_account" || v === "bank") return "BankAccount";
      return "Crypto";
    },

    displayValue(w) {
      if (!w) return "";
      var type = this.normalizeType(w.walletType);
      if (type === "BankAccount") return w.bankAccount ? String(w.bankAccount) : "";
      return w.walletHash ? String(w.walletHash) : "";
    },

    fetchWallets() {
      this.loadingList = true;
      this.error = "";
      axios
        .get(apiDomain + "/exchange/digitalwallet/list", { headers: getHeader() })
        .then((res) => {
          this.wallets = Array.isArray(res.data) ? res.data : [];
        })
        .catch((e) => {
          console.error(e);
          this.error = "Не удалось загрузить список кошельков. Попробуйте позже.";
        })
        .finally(() => {
          this.loadingList = false;
        });
    },

    startEdit(w) {
      this.editingId = w.digitalWalletId;
      this.editingName = w.walletName || "";
    },
    cancelEdit() {
      this.editingId = null;
      this.editingName = "";
    },
    saveRename(w) {
      const name = String(this.editingName || "").trim();
      if (!name) {
        this.toast("Название не может быть пустым.", "Ошибка", "warning");
        return;
      }
      this.editingBusy = true;
      axios.post(
        apiDomain + "/exchange/digitalwallet/" + w.digitalWalletId + "/rename",
        { walletName: name },
        { headers: getHeader() },
      )
        .then(() => {
          // Локальный state обновляем сразу — избегаем нового GET на всю
          // страницу. Позже fetchWallets() всё равно подтянет свежие
          // данные при следующей операции.
          const idx = this.wallets.findIndex(x => x.digitalWalletId === w.digitalWalletId);
          if (idx !== -1) this.$set(this.wallets, idx, { ...this.wallets[idx], walletName: name });
          this.toast("Название обновлено.", "Готово", "success");
          this.cancelEdit();
        })
        .catch(() => { this.toast("Не удалось переименовать.", "Ошибка", "danger"); })
        .finally(() => { this.editingBusy = false; });
    },
    confirmDelete(w) {
      // Открываем модалку. deleteTarget сохраняет кошелёк, чтобы
      // в шапке модалки отобразить имя и адрес до подтверждения.
      this.deleteTarget = w;
    },
    closeDeleteModal() {
      this.deleteTarget = null;
    },
    submitDelete() {
      if (!this.deleteTarget) return;
      const w = this.deleteTarget;
      this.deleteBusy = true;
      axios.post(
        apiDomain + "/exchange/digitalwallet/" + w.digitalWalletId + "/delete",
        {},
        { headers: getHeader() },
      )
        .then(() => {
          this.wallets = this.wallets.filter(x => x.digitalWalletId !== w.digitalWalletId);
          this.toast("Кошелёк удалён.", "Готово", "success");
          this.closeDeleteModal();
        })
        .catch(() => { this.toast("Не удалось удалить.", "Ошибка", "danger"); })
        .finally(() => { this.deleteBusy = false; });
    },
    createWallet() {
      this.error = "";
      this.success = "";
      if (!this.canSubmit) {
        this.error = "Заполните все поля перед сохранением.";
        return;
      }

      this.creating = true;

      var type = this.form.walletType;
      var payload = {
        userId: this.safeUserId,
        walletType: type,
        walletName: String(this.form.walletName || "").trim(),
        bankAccount: null,
        walletHash: null,
        assetCode: null,
        networkCode: null,
        bik: null
      };

      if (type === "BankAccount") {
        payload.bankAccount = String(this.primaryValue || "").trim();
        payload.bik = String(this.form.bik || "").trim();
      } else {
        payload.walletHash = String(this.primaryValue || "").trim();
        payload.assetCode = this.form.assetCode || null;
        payload.networkCode = this.form.networkCode || null;
      }

      axios
        .post(apiDomain + "/exchange/digitalwallet/create", payload, { headers: getHeader() })
        .then(() => {
          this.success = "Кошелёк успешно создан.";
          this.toast("Кошелёк успешно создан.", "Готово", "success");
          this.resetForm();
          this.showCreate = false;
          this.fetchWallets();
          // Honour the returnTo flag from BuyCrypto's empty-state — the
          // user came here to unblock an order, not to manage wallets,
          // so we bounce them back automatically. Only same-origin
          // paths are followed (defence against open-redirect via URL).
          const returnTo = this.$route.query.returnTo;
          if (typeof returnTo === "string" && returnTo.startsWith("/")) {
            this.$router.push(returnTo);
          }
        })
        .catch((e) => {
          console.error(e);
          this.error = "Не удалось создать кошелёк. Проверьте данные и попробуйте снова.";
          this.toast(this.error, "Ошибка", "danger");
        })
        .finally(() => {
          this.creating = false;
        });
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString("ru-RU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    toast(message, title, variant) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: title || "Сообщение",
          variant: variant || "info",
          solid: true,
          toaster: "b-toaster-top-center",
          autoHideDelay: 2600
        });
      }
    },

    copyToClipboard(text) {
      var value = text ? String(text) : "";
      if (!value) {
        this.toast("Нечего копировать.", "Внимание", "warning");
        return;
      }

      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(
          () => this.toast("Скопировано в буфер обмена.", "Готово", "success"),
          () => this.fallbackCopy(value)
        );
        return;
      }
      this.fallbackCopy(value);
    },

    fallbackCopy(value) {
      try {
        var el = document.createElement("textarea");
        el.value = value;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.toast("Скопировано в буфер обмена.", "Готово", "success");
      } catch (e) {
        this.toast("Не удалось скопировать. Скопируйте вручную.", "Ошибка", "danger");
      }
    },

    pasteFromClipboard() {
      if (navigator && navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText().then(
          (txt) => {
            if (txt) this.primaryValue = txt;
          },
          () => {
            this.toast("Не удалось вставить из буфера обмена.", "Ошибка", "warning");
          }
        );
      } else {
        this.toast("Вставка из буфера обмена не поддерживается в этом браузере.", "Внимание", "warning");
      }
    }
  },
  created() {
    this.fetchWallets();
    this.fetchCatalog();
    // BuyCrypto's empty-state CTA passes ?intent=crypto|bank — we
    // preselect the wallet type and unfurl the create panel so the
    // user lands directly on the right form.
    const intent = this.$route.query.intent;
    if (intent === "crypto" || intent === "bank") {
      this.form.walletType = intent === "bank" ? "BankAccount" : "Crypto";
      this.showCreate = true;
    }
  }
};
</script>

<style scoped>
/* Similar dark UX like your balance page */
.dw-page{ margin-top: 18px; }

/* Titles */
.page-title{ color:#FCFCFD; font-weight:800; letter-spacing:-0.3px; margin-top:6px; }
.page-subtitle{ color:#B1B5C3; font-size:13px; }
.subtitle-strong{ color:#E6E8EC; font-weight:700; }

/* Buttons */
.btn-round{ border-radius:12px; padding:8px 14px; }
.btn-primary-soft{ font-weight:800; box-shadow:0 18px 40px rgba(99,102,241,0.18); }
.btn-success-soft{ font-weight:800; box-shadow:0 18px 40px rgba(34,197,94,0.16); }
.btn-x{ border-radius:12px; }

/* Create card */
.create-card{
  background: rgba(24, 25, 29, 0.55);
  border-radius: 16px;
  padding: 18px;
  border: 1px solid rgba(53, 57, 69, 0.85);
}
.create-title{ color:#FCFCFD; font-weight:900; letter-spacing:-0.2px; }
.create-sub{ color:#B1B5C3; font-size:13px; }

/* Inputs (dark) */
.field-label{
  color:#E6E8EC;
  font-weight:800;
  font-size:12px;
  letter-spacing:.2px;
  text-transform:uppercase;
  margin-bottom:6px;
}
.field-hint{ color:#B1B5C3; font-size:12px; margin-top:6px; }
.dark-input{
  background: rgba(35, 38, 47, 0.72) !important;
  border: 1px solid rgba(53, 57, 69, 0.9) !important;
  color: #FCFCFD !important;
  border-radius: 14px !important;
  height: 44px;
}
.dark-input:focus{
  box-shadow: 0 0 0 0.2rem rgba(239,185,20,0.15) !important;
  border-color: rgba(239,185,20,0.55) !important;
}
/* Read-only "слот" used for single-network assets — visually matches
   .dark-input but indicates the value is auto-derived from asset. */
.dark-input--readonly{
  display: flex; align-items: center;
  color: #B1B5C3 !important;
  padding: 0 14px;
  cursor: default;
}
.dark-input-group :deep(.input-group-append .btn){ height:44px; border-radius:14px; }
.btn-ghost{
  border: 1px solid rgba(255,255,255,0.16) !important;
  background: rgba(255,255,255,0.03) !important;
  color: #E6E8EC !important;
  font-weight: 800;
}

/* Alerts */
.alert-soft{ border-radius:14px; padding:12px 14px; font-weight:700; font-size:13px; }
.alert-soft.danger{
  color:#fecaca;
  background: rgba(239, 68, 68, 0.10);
  border: 1px solid rgba(239, 68, 68, 0.22);
}
.alert-soft.success{
  color:#bbf7d0;
  background: rgba(34, 197, 94, 0.10);
  border: 1px solid rgba(34, 197, 94, 0.22);
}
.small-muted{ color:#B1B5C3; font-size:13px; }

/* ✅ Split sections */
.dw-split{
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}
@media (min-width: 992px){
  .dw-split{
    grid-template-columns: 1fr 1fr; /* side-by-side on desktop */
    align-items: stretch; /* обе секции одинаковой высоты */
  }
}

.dw-section{
  border-radius: 18px;
  padding: 14px 12px 4px;
  border: 1px solid rgba(53, 57, 69, 0.70);
  background: rgba(17, 18, 22, 0.20);
  display: flex;
  flex-direction: column;
}
/* Ряд с карточками растягивается на остаток высоты секции, чтобы
   одиночная карточка (напр. банковский счёт) занимала ту же высоту,
   что и колонка с несколькими крипто-кошельками. */
.dw-section > .row{ flex: 1 1 auto; }
.dw-section.bank{
  box-shadow: 0 18px 44px rgba(59, 130, 246, 0.08);
}
.dw-section.crypto{
  box-shadow: 0 18px 44px rgba(239, 185, 20, 0.10);
}

.dw-section-head{
  padding: 6px 6px 14px;
}
.dw-sec-title{
  color:#FCFCFD;
  font-weight: 950;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.dw-sec-count{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  height: 22px;
  min-width: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  color: #E6E8EC;
}
.dw-sec-sub{
  margin-top: 6px;
  color: #B1B5C3;
  font-size: 12px;
}

/* Wallet card list */
.dw-card{
  background: rgba(24, 25, 29, 0.55);
  border-radius: 16px;
  padding: 18px;
  border: 1px solid rgba(53, 57, 69, 0.85);
  transition: box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}
/* Мета-блок прижимаем к низу — карточки одинаковой высоты выглядят
   единообразно независимо от длины адреса/названия. */
.dw-card .dw-meta{ margin-top: auto; }
.dw-card:hover{
  background: rgba(35, 38, 47, 0.72);
  box-shadow: 0 10px 24px rgba(0,0,0,0.18);
  transform: translateY(-2px);
}

/* ✅ different colors by type */
.dw-card-bank{
  border-color: rgba(59,130,246,0.22);
  background: linear-gradient(135deg, rgba(59,130,246,0.10), rgba(24,25,29,0.55));
}
.dw-card-crypto{
  border-color: rgba(239,185,20,0.28);
  background: linear-gradient(135deg, rgba(239,185,20,0.12), rgba(24,25,29,0.55));
}

.dw-header{ display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
.meta-muted{ color:#B1B5C3; }

.type-badge{
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.03);
}
.badge-bank{
  color: #93c5fd;
  border-color: rgba(147,197,253,0.25);
  background: rgba(59,130,246,0.10);
}
.badge-crypto{
  color: #EFB914;
  border-color: rgba(239,185,20,0.35);
  background: rgba(239,185,20,0.12);
}
.badge-net {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  background: rgba(255,255,255,0.06);
  color: #B1B5C3;
  letter-spacing: 0.02em;
}
.dw-actions { display: flex; gap: 4px; }
.dw-icon-btn {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.08);
  color: #B1B5C3;
  border-radius: 8px;
  width: 30px; height: 30px;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
  line-height: 1;
}
.dw-icon-btn:hover {
  background: rgba(255,255,255,0.05);
  color: #FCFCFD;
}
.dw-icon-btn-danger:hover {
  background: rgba(239,68,68,0.12);
  color: #F87171;
  border-color: rgba(239,68,68,0.35);
}
.dw-title-edit {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.dw-title-edit .dark-input { flex: 1; }

.dw-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.dw-modal {
  background: #1a1d22;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}
.dw-modal-head {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  color: #FCFCFD;
  font-weight: 800;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dw-modal-close {
  background: transparent;
  border: 0;
  color: #777e90;
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  line-height: 1;
}
.dw-modal-close:hover { color: #FCFCFD; }
.dw-modal-body {
  padding: 18px 20px;
  color: #B1B5C3;
  font-size: 13px;
  line-height: 1.5;
}
.dw-modal-name {
  color: #FCFCFD;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 6px;
}
.dw-modal-addr {
  color: #777e90;
  font-size: 12px;
  word-break: break-all;
}
.dw-modal-foot {
  padding: 14px 20px;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: flex-end;
}

.dw-title{ color:#FCFCFD; font-weight:900; font-size:16px; letter-spacing:-0.2px; margin-bottom:10px; }

.dw-value{
  display:flex;
  align-items:center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 12px;
  min-height: 48px;
}
.mono{
  color:#E6E8EC;
  font-weight:800;
  font-size:13px;
  word-break: break-all;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace;
}
.btn-copy{
  border-radius: 12px;
  padding: 6px 10px;
  border: 1px solid rgba(255,255,255,0.16) !important;
  background: rgba(255,255,255,0.03) !important;
  color: #E6E8EC !important;
}
.btn-copy-bank{
  border-color: rgba(59,130,246,0.35) !important;
}
.btn-copy-crypto{
  border-color: rgba(239,185,20,0.45) !important;
}

/* Meta */
.dw-meta{ font-size:13px; color:#B1B5C3; }
.dw-meta div{ margin-bottom:6px; }
.label{ font-weight:800; color:#E6E8EC; margin-right:6px; }
.value{ color:#B1B5C3; }

/* Empty */
.empty{
  margin-top: 28px;
  text-align: center;
  padding: 30px;
  border: 1px dashed rgba(177, 181, 195, 0.35);
  border-radius: 16px;
  background: rgba(24, 25, 29, 0.35);
}
.empty-icon{ font-size:34px; margin-bottom:10px; }
.empty-title{ color:#FCFCFD; font-weight:800; margin-bottom:4px; }
.empty-subtitle{ color:#B1B5C3; font-size:13px; }

.empty-mini{
  margin: 0 8px 14px;
  padding: 12px 14px;
  border-radius: 14px;
  font-weight: 800;
  font-size: 13px;
  border: 1px dashed rgba(255,255,255,0.14);
  color: #B1B5C3;
  background: rgba(0,0,0,0.14);
}
.empty-mini-bank{
  border-color: rgba(59,130,246,0.22);
}
.empty-mini-crypto{
  border-color: rgba(239,185,20,0.26);
}
</style>
